import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { TrashIcon } from '@phosphor-icons/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from 'src/context/ToastContext';
import { useDeleteArDepositBatch } from '@/api/rpc/hooks/accounts-receivable/useArDepositBatches';

/**
 * Throws a draft check deposit away, after a confirmation. `compact` is the
 * icon-only form for a table row.
 */
export function DeleteDepositButton({
  depositId,
  compact = false,
  onDeleted,
}: {
  depositId: string;
  compact?: boolean;
  onDeleted?: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const deleteDeposit = useDeleteArDepositBatch();

  async function handleConfirm() {
    try {
      await deleteDeposit.mutateAsync({ id: depositId });
      setOpen(false);
      showToast({
        color: 'success',
        icon: 'check',
        title: gt('Deposit deleted', {
          context: 'toast title, a draft check deposit was deleted',
        }),
      });
      onDeleted?.();
    } catch (error) {
      showToast({
        color: 'error',
        title: gt('Could not delete the deposit', {
          context: 'toast title, deleting a draft check deposit failed',
        }),
        message: error instanceof Error ? error.message : '',
      });
    }
  }

  const label = gt('Delete deposit', {
    context: 'button, delete a draft check deposit',
  });
  return (
    <>
      {compact ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          title={label}
          onClick={(event) => {
            // The row itself opens the deposit.
            event.stopPropagation();
            setOpen(true);
          }}
        >
          <TrashIcon className="size-4" />
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <TrashIcon className="size-4" />
          {label}
        </Button>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={(event) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm deleting a draft check deposit">
                Delete this deposit?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="explanation of what deleting a draft check deposit does">
                The deposit and its files are removed. Remittances already read
                from them stay in the remittance queue. Nothing was submitted,
                so no cash receipts are affected.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDeposit.isPending}>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleConfirm();
              }}
              disabled={deleteDeposit.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              <T context="confirm button, delete a draft check deposit">
                Delete
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
